/**
 * BeginProductionCTA — Sprint 5 · Project Symphony
 *
 * The main call-to-action that opens the production dialog.
 *
 * Renders as:
 * * A sticky primary button when the campaign is in
 *   `strategy_approved` state.
 * * A subtle status indicator when generation is running or complete.
 */
import { useState } from "react";
import { Sparkles } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { Card } from "@/shared/ui/card";
import { BeginProductionDialog } from "./BeginProductionDialog";

export function BeginProductionCTA({
  blueprintId,
  totalAssets,
  campaignStatusCode,
}: {
  blueprintId: string;
  totalAssets: number;
  campaignStatusCode: string;
}): JSX.Element | null {
  const [open, setOpen] = useState(false);

  if (campaignStatusCode !== "strategy_approved") return null;

  return (
    <section data-testid="begin-production-cta">
      <Card className="p-6 sm:p-8 border-l-4 border-l-primary bg-primary/[0.03]">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
              Ready to Begin
            </p>
            <h3 className="text-lg sm:text-xl font-semibold mb-1">
              Your marketing department is standing by.
            </h3>
            <p className="text-sm text-muted-foreground">
              Approve the go-ahead and the Content Team will queue the work
              for the Creative Studio.
            </p>
          </div>
          <Button
            type="button"
            size="lg"
            onClick={() => setOpen(true)}
            data-testid="begin-production-button"
          >
            <Sparkles className="h-4 w-4 mr-2" aria-hidden />
            Begin Campaign Production
          </Button>
        </div>
      </Card>

      <BeginProductionDialog
        open={open}
        onOpenChange={setOpen}
        blueprintId={blueprintId}
        totalAssets={totalAssets}
      />
    </section>
  );
}
